import { useState, useCallback } from "react";
import type {
  FlowVersionSummaryViewModel,
  FlowConfigViewModel,
  CreateFlowConfigRequest,
  ActivateFlowRequest,
} from "@models/doctype";
import type { ProviderViewModel } from "@models/provider";
import type { ModelViewModel, ModelBasicInfoViewModel } from "@models/model";
import { docTypeApi } from "@src/services/doctypeApi";
import { providerApi } from "@src/services/providerApi";

export interface IFlowConfigState {
  flowVersions: FlowVersionSummaryViewModel[];
  selectedFlowConfig: FlowConfigViewModel | null;
  providers: ProviderViewModel[];
  isLoading: boolean;
  error: string | null;
  fetchFlowVersions: (docTypeId: string) => Promise<void>;
  fetchFlowDetail: (
    docTypeId: string,
    flowBaseId: string,
    version: number,
  ) => Promise<FlowConfigViewModel | null>;
  createFlowConfig: (
    docTypeId: string,
    data: CreateFlowConfigRequest,
  ) => Promise<FlowConfigViewModel | null>;
  activateFlow: (
    docTypeId: string,
    data: ActivateFlowRequest,
  ) => Promise<FlowConfigViewModel | null>;
  fetchProviders: (providerType: string) => Promise<ProviderViewModel[]>;
  fetchModelsBasicByProviderKey: (
    providerKey: string,
  ) => Promise<ModelBasicInfoViewModel[]>;
  fetchModelDetailByName: (modelName: string) => Promise<ModelViewModel | null>;
}

export const useFlowConfig = (): IFlowConfigState => {
  const [flowVersions, setFlowVersions] = useState<
    FlowVersionSummaryViewModel[]
  >([]);
  const [selectedFlowConfig, setSelectedFlowConfig] =
    useState<FlowConfigViewModel | null>(null);
  const [providers, setProviders] = useState<ProviderViewModel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFlowVersions = useCallback(async (docTypeId: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const versions = await docTypeApi.getFlowVersions(docTypeId);
      setFlowVersions(versions);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch flow versions";
      setError(errorMessage);
      console.error("Error fetching flow versions:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchFlowDetail = useCallback(
    async (
      docTypeId: string,
      flowBaseId: string,
      version: number,
    ): Promise<FlowConfigViewModel | null> => {
      try {
        setIsLoading(true);
        setError(null);
        const config = await docTypeApi.getFlowDetailByVersion(
          docTypeId,
          flowBaseId,
          version,
        );
        setSelectedFlowConfig(config);
        return config;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to fetch flow detail";
        setError(errorMessage);
        console.error("Error fetching flow detail:", err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const createFlowConfig = useCallback(
    async (
      docTypeId: string,
      data: CreateFlowConfigRequest,
    ): Promise<FlowConfigViewModel | null> => {
      try {
        setIsLoading(true);
        setError(null);
        const newConfig = await docTypeApi.createFlowConfig(docTypeId, data);
        await fetchFlowVersions(docTypeId);
        return newConfig;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to create flow config";
        setError(errorMessage);
        console.error("Error creating flow config:", err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchFlowVersions],
  );

  const activateFlow = useCallback(
    async (
      docTypeId: string,
      data: ActivateFlowRequest,
    ): Promise<FlowConfigViewModel | null> => {
      try {
        setIsLoading(true);
        setError(null);
        const activatedConfig = await docTypeApi.activateFlow(docTypeId, data);
        await fetchFlowVersions(docTypeId);
        return activatedConfig;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to activate flow";
        setError(errorMessage);
        console.error("Error activating flow:", err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchFlowVersions],
  );

  const fetchProviders = useCallback(
    async (providerType: string): Promise<ProviderViewModel[]> => {
      try {
        const response = await providerApi.getProviders({
          providerType,
          status: "active",
        });
        setProviders(response.items);
        return response.items;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to fetch providers";
        setError(errorMessage);
        console.error("Error fetching providers:", err);
        return [];
      }
    },
    [],
  );

  const fetchModelsBasicByProviderKey = useCallback(
    async (providerKey: string): Promise<ModelBasicInfoViewModel[]> => {
      try {
        const models =
          await providerApi.getModelsBasicByProviderKey(providerKey);
        return models;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to fetch models";
        setError(errorMessage);
        console.error("Error fetching models by provider key:", err);
        return [];
      }
    },
    [],
  );

  const fetchModelDetailByName = useCallback(
    async (modelName: string): Promise<ModelViewModel | null> => {
      try {
        const model = await providerApi.getModelDetailByName(modelName);
        return model;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to fetch model detail";
        setError(errorMessage);
        console.error("Error fetching model detail:", err);
        return null;
      }
    },
    [],
  );

  return {
    flowVersions,
    selectedFlowConfig,
    providers,
    isLoading,
    error,
    fetchFlowVersions,
    fetchFlowDetail,
    createFlowConfig,
    activateFlow,
    fetchProviders,
    fetchModelsBasicByProviderKey,
    fetchModelDetailByName,
  };
};
